import {
	CountryCode,
	FinancialServicesUIProvider,
	PaymentMethod,
	SessionProvider,
	SimplePicker,
} from '@shipstation/financial-services-ui-components';
import { Modal, Utils } from 'common';
import React, {
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useState,
} from 'react';
import AuctanePayCommunication from '../../utils/AuctanePayCommunication';
import APapiConfig from './APapiConfig';

import { BillingAddress } from './BillingAddress.component';

const { ModalContext } = Modal;

const { Session: SdcSession } = Utils;
const getBillingInfoFromAccount = () => {
	const { meterDetails } = SdcSession.getAccountInfo();
	if (!meterDetails?.meterAddress) return undefined;

	const { meterAddress } = meterDetails;
	return {
		fullName: meterAddress.name,
		line1: meterAddress.addressLine1,
		line2: meterAddress.addressLine2 || '',
		line3: meterAddress.addressLine3 || '',
		city: meterAddress.city,
		state: meterAddress.stateProvince,
		postalCode: meterAddress.postalCode,
		countryCode: meterAddress.countryCode || 'US',
		company: meterAddress.companyName || '',
		firstName: meterAddress.name.split(' ')[0] || '',
		lastName: meterAddress.name.split(' ').slice(1).join(' ') || '',
		phone: meterAddress.phone,
		email: meterAddress.email || '',
	};
};

const AddPaymentMethod = forwardRef((props, ref) => {
	const [session, setSession] = useState(null);
	const [activePayment, setActivePayment] = useState(
		props.autoSelectPayByBank
			? PaymentMethod.PayByBankUS
			: PaymentMethod.CreditCard
	);
	const [creditCardSumbit, setCreditCardSumbit] = useState(() => {});
	const [payByBankSubmit, setPayByBankSubmit] = useState(() => {});
	const { customerId } = SdcSession.getAccountInfo();
	let { corporationId } = SdcSession.getAccountInfo();
	const billingInfo = getBillingInfoFromAccount();
	const { callback, isForCorpAccount } = props;
	const { showError } = useContext(ModalContext);

	if (!isForCorpAccount) {
		// Treat as a non-corporate account for Location Settings
		corporationId = 0;
	}

	useEffect(() => {
		const getSession = async () => {
			const response = await AuctanePayCommunication.getAPSession({
				customerId,
				corporationId,
			});
			setSession(response.data);
		};
		getSession();
	}, []);

	useEffect(() => {
		const { onPaymentTypeChange } = props;
		onPaymentTypeChange && onPaymentTypeChange(activePayment);
	}, [activePayment]);

	// Transforming indexes to payment method values
	const transformedSession = React.useMemo(() => {
		if (!session?.eligiblePaymentMethodTypes) return session;

		const paymentMethodValues = Object.values(PaymentMethod);
		const mappedMethods = Array.isArray(session.eligiblePaymentMethodTypes)
			? session.eligiblePaymentMethodTypes
					.map(index => paymentMethodValues[index])
					.filter(Boolean)
					.filter(method => method !== PaymentMethod.PayPal) // Remove PayPal since it's not supported
			: [];

		// Temp fix for PayByBankUS WEBAPPS-45227
		if (mappedMethods.includes(PaymentMethod.Stamps)) {
			mappedMethods.splice(mappedMethods.indexOf(PaymentMethod.Stamps), 1);
			mappedMethods.push(PaymentMethod.PayByBankUS);
		}

		return {
			...session,
			eligiblePaymentMethodTypes: mappedMethods,
		};
	}, [session]);

	const submitCreditCardInit = auctanePaySumbitPassthrough => {
		setCreditCardSumbit(() => auctanePaySumbitPassthrough);
	};

	const submitPayByBankInit = auctanePaySumbitPassthrough => {
		setPayByBankSubmit(() => auctanePaySumbitPassthrough);
	};

	const onPaymentCompleted = result => {
		if (result.resultCode === 'Authorised') {
			callback({
				status: 'success',
			});
		} else {
			return showError({
				data: {
					title: 'Error',
					description: result.resultCode,
					primaryButtonText: 'Close',
					handleType: 'custom',
				},
			});
		}
	};

	// Credit card configuration
	const creditCardConfig = {
		submitPayment: submitCreditCardInit,
		onPaymentCompleted,
		onError: (step, method, error) =>
			console.error('Credit card error:', step, method, error),
	};

	// Pay by bank configuration
	const payByBankConfig = {
		submitPayment: submitPayByBankInit,
		onError: (step, method, error) =>
			console.error('Pay by bank error:', step, method, error),
		beforeRedirect: async () => console.log('Before redirect'),
		returnUrl: window.location.origin + window.location.pathname,
	};

	// Handle payment submission
	const handleSubmit = () => {
		if (activePayment === PaymentMethod.CreditCard && creditCardSumbit) {
			creditCardSumbit();
		} else if (activePayment === PaymentMethod.PayByBankUS && payByBankSubmit) {
			payByBankSubmit();
		}
	};

	// Expose handleSubmit function to parent component
	useImperativeHandle(ref, () => ({
		handleSubmit,
	}));

	if (!session) {
		return <div>Loading...</div>;
	}

	return (
		<SessionProvider initialSession={transformedSession}>
			<FinancialServicesUIProvider
				apiConfig={APapiConfig}
				initialSession={transformedSession}
				billingInfo={billingInfo}
				userCountryCode={CountryCode.US}
				currencyCode="USD"
				locale="en"
				useMantine={true}
			>
				<SimplePicker
					acceptedPaymentTypes={transformedSession?.eligiblePaymentMethodTypes}
					creditCardConfig={creditCardConfig}
					payByBankConfig={payByBankConfig}
					locale="en"
					currencyCode="USD"
					onPaymentTypeChange={setActivePayment}
					showTitle={false}
				/>
				<BillingAddress accountBillingInfo={billingInfo} />
			</FinancialServicesUIProvider>
		</SessionProvider>
	);
});

export default AddPaymentMethod;
